/**
 * ImpersonationBanner — a persistent danger-toned bar shown while you are signed in AS
 * another user (true server-side impersonation; see auth/impersonation.ts).
 * "Stop impersonating" re-authenticates as the original admin.
 */
import { useState } from 'react';
import { useProfile } from '../profile/ProfileProvider';
import { getImpersonation, stopImpersonation } from '../auth/impersonation';

export function ImpersonationBanner() {
  const { state } = useProfile();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const flag = getImpersonation();
  const active =
    !!flag &&
    state.status === 'ready' &&
    state.user.email.toLowerCase() === flag.targetEmail.toLowerCase();
  if (!active || !flag) return null;

  const stop = async () => {
    setErr(null);
    setBusy(true);
    try {
      await stopImpersonation();
      // On success the auth state flips back to the admin; this component
      // unmounts. If the popup was cancelled we stay impersonating.
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Could not stop impersonating.');
      setBusy(false);
    }
  };

  return (
    <div className="bg-danger text-primary-fg">
      <div className="px-6 py-2 flex items-center justify-between gap-4 text-sm">
        <span>
          Impersonating <strong>{flag.targetName}</strong> ({flag.targetEmail}) — you are acting as
          this person, and changes are saved as them.
          {err && <span className="ml-2 underline">{err}</span>}
        </span>
        <button
          type="button"
          onClick={() => void stop()}
          disabled={busy}
          className="shrink-0 font-medium underline underline-offset-2 hover:no-underline disabled:opacity-60"
        >
          {busy ? 'Stopping…' : 'Stop impersonating'}
        </button>
      </div>
    </div>
  );
}
